import React from 'react';
import { batch, useSelector } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import router from 'next/router';
import { CarDetailsService, DealService, getOptionsList, IFeature } from '../../services';
import { handleInitialURLParams } from '@utils/page.utils';
import { ConfiguratorDuck } from '../../redux/configurator/configurator.duck';
import { SSRError } from '@components/SSRError';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import CarDetailsDuck from '../../redux/carDetails/carDetails.duck';
import FinanceWidgetDuck from '../../redux/financeWidget/financeWidget.duck';
import { NextPage } from 'next';
import { Configurator } from '@components/v2/Configurator';
import { PaymentJourneyTypes } from '../../partExchange/interfaces/Default';
import { hasFinanceVariants } from '@utils/Price.utils';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { handleFinanceQuote, updateGeneralContext } from '@utils/getInitialProps/common';
import uniq from 'lodash/uniq';
import { getMatchingOptionsFromMandatoryOptsString } from '@utils/mandatoryOptions.utils';
import { ContextWithStore } from '@utils/getInitialProps/@types';
import FilterDuck from 'src/redux/filters/filter.duck';
import { getSessionIdCookie } from '@utils/Session.utils';
import {LEASYS_PROVIDER_TYPE, SESSION_COOKIE_NAME} from 'src/constants/main';
import DealerDuck from 'src/redux/dealer/dealer.duck';
import { IConfigurableCar, IStockCar } from 'src/interfaces/Car';
import TrimSelectorDuck from "../../redux/trimSelector/trimSelector.duck";
import {INITIAL_PAGE_NUMBER} from "../trim/stock/[...model]";

interface IConfiguratorPage {
    statusCode?: number;
}

const parseOptionIdsParam = (params: string | string[]) => {
    let selectedOptionIds: string[] = [];
    if (params && Array.isArray(params)) {
        selectedOptionIds = params;
    } else if (params) {
        selectedOptionIds = (params as string)?.split(',');
    }

    return selectedOptionIds;
};

const ConfiguratorPage: NextPage<IConfiguratorPage> = ({ statusCode, ...props }) => {
    if (!statusCode || statusCode > 201) return <SSRError statusCode={statusCode} />;

    return <Configurator {...props} />;
};

ConfiguratorPage.getInitialProps = async (ctx: ContextWithStore) => {
    const { store, query, res, asPath, featureContext, req } = await updateGeneralContext(ctx);
    const token = req?.cookies?.[SESSION_COOKIE_NAME] ?? getSessionIdCookie();
    let statusCode = undefined;
    const withError = (initialFirstRender: boolean, statusCode: undefined | number) => ({
        initialFirstRender,
        namespacesRequired: ['common'],
        hash: '',
        statusCode,
        featureContext,
    });

    const initialFirstRender = true;

    const { config } = query;
    try {
        if (!config) console.log('NO CONFIG', query);

        const defaultPaymentJourney = featureContext[FEATURES_LIST.FEATURE_SWITCH_DEFAULT_PAYMENT_JOURNEY];
        const paymentJourneyLocked = !featureContext[FEATURES_LIST.FEATURE_SWITCH_BUDGET_CHANGE_ALLOWED];

        const nameplateBodyStyle = config[1];
        const specPack = config[2];
        const engineGearboxFuel = config[3];
        const exteriorColor = config[4];
        const interiorColor = config[5];
        const queryString = config[6];

        const selectedOptions = ConfiguratorDuck.getOwnState(store.getState()).selectedOptions;
        let currentCarDetails = CarDetailsDuck.getOwnState(store.getState()).currentCarDetails;
        let currentDeal = CarDetailsDuck.getCurrentDeal<IStockCar | IConfigurableCar>(store.getState());
        const dealer = DealerDuck.getOwnState(store.getState()).dealer;
        const isDealerSelected =
            currentCarDetails.dealers?.[0] || !!dealer?.externalId || !!currentDeal?.dealerId || !!dealer?.city;

        // APP-25596
        // if dealer was selected in dealer selection page and user is going back from basket to config page
        // we need to remove selected dealer and update the re-calculated prices
        if (isDealerSelected) {
            try {
                const { data } = await DealService.deleteDealDealer(token);

                if (data.deal) currentDeal = data.deal;
                currentCarDetails = currentDeal.fullProductConfiguration;

                if (data.financeQuote && data.financeQuote?.providerType !== LEASYS_PROVIDER_TYPE) {
                    store.dispatch(FinanceWidgetDuck.setFinanceQuote(data.financeQuote));
                }
                if (currentDeal) {
                    store.dispatch(CarDetailsDuck.setCurrentDealAndCarDetail(currentDeal));
                }
            } catch (error) {
                // noop
            }
            store.dispatch(DealerDuck.setDealer(null));
        }

        if (
            !(
                currentCarDetails &&
                currentCarDetails.exteriorColourSlug === exteriorColor &&
                currentCarDetails.interiorColourSlug === interiorColor &&
                specPack === currentCarDetails.specPackSlug &&
                currentCarDetails.engineGearboxFuelSlug === engineGearboxFuel &&
                currentCarDetails.nameplateBodyStyleSlug === nameplateBodyStyle
            )
        ) {
            batch(() => {
                store.dispatch(FinanceWidgetDuck.setFinanceWidgetOutput(null));
                store.dispatch(FinanceWidgetDuck.setFinancePricesList(null));
                store.dispatch(FinanceWidgetDuck.setFinanceQuote(null));
                store.dispatch(CarDetailsDuck.setCurrentDeal(null));
            });
        }

        const urlParams = new URLSearchParams(queryString);
        const optionsSelectedParams = query?.options || urlParams.get('options');
        const financeQuoteId = query?.financeQuoteId || urlParams.get('financeQuoteId');
        const isDealSaved: boolean = Boolean(query?.saved || urlParams.get('saved'));

        if (financeQuoteId) {
            store.dispatch(UIDuck.setLoading(1));
            await handleFinanceQuote(store, financeQuoteId);
            store.dispatch(UIDuck.setLoading(-1));
        }

        let selectedOptionIds: string[] = parseOptionIdsParam(optionsSelectedParams);

        const { vehicle, features } = await CarDetailsService.getCarDetailsWithAllOptions(
            nameplateBodyStyle,
            specPack,
            engineGearboxFuel,
            exteriorColor,
            interiorColor,
            query?.reSaveOptionsOnVehicle && selectedOptions?.length > 0 ? selectedOptions : [],
            true
        );

        if (!vehicle) return withError(initialFirstRender, statusCode);
        statusCode = 201;

        const journeyTypeParam = query?.journey || urlParams.get('journey');
        const hasFinance = hasFinanceVariants(vehicle.prices);
        const paymentJourneyLockedToFinance =
            paymentJourneyLocked && defaultPaymentJourney === PaymentJourneyTypes.FINANCE;

        if (featureContext[FEATURES_LIST.FEATURE_SWITCH_MANDATORY_OPTIONS]) {
            selectedOptionIds = uniq([
                ...selectedOptionIds,
                ...getMatchingOptionsFromMandatoryOptsString(
                    featureContext[FEATURES_LIST.FEATURE_SWITCH_MANDATORY_OPTIONS],
                    vehicle.lcdv16
                ),
            ]);

            // possibleOptions is empty array on freshly imported car, options list then contains possibleOptions we need for filtering
            if (selectedOptionIds.length && !vehicle.possibleOptions.length) {
                vehicle.possibleOptions = await getOptionsList(
                    vehicle.externalId,
                    query?.reSaveOptionsOnVehicle && selectedOptions?.length > 0 ? selectedOptions : [],
                    hasFinance ? PaymentJourneyTypes.FINANCE : PaymentJourneyTypes.CASH
                )
                    .then((optionsListResponse) => optionsListResponse?.data?.options ?? [])
                    .catch(() => []);
            }

            selectedOptionIds = selectedOptionIds.filter((optionId: string) =>
                vehicle.possibleOptions.find(({ id }: { id: string }) => id === optionId)
            );
        }

        handleInitialURLParams(store, asPath, res, router, featureContext);

        batch(() => {
            store.dispatch(UIDuck.setIsDealSaved(isDealSaved));
            store.dispatch(ConfiguratorDuck.setOptions(vehicle.possibleOptions));
            store.dispatch(ConfiguratorDuck.setAvailableOptions([]));
            store.dispatch(CarDetailsDuck.setCurrentCarDetails(vehicle));
            store.dispatch(ConfiguratorDuck.setExteriorColors(features.exteriorColors));
            store.dispatch(ConfiguratorDuck.setInteriorColors(features.interiorColors));
            store.dispatch(ConfiguratorDuck.setMotorizations(features.engines));
            store.dispatch(
                ConfiguratorDuck.setExteriorColor(
                    features.exteriorColors.find((extColor: IFeature) => extColor.id === vehicle.exteriorColour.id)
                )
            );
            store.dispatch(
                ConfiguratorDuck.setInteriorColor(
                    features.interiorColors.find((intColor: IFeature) => intColor.id === vehicle.interiorColour.id)
                )
            );
            store.dispatch(ConfiguratorDuck.setMotorizationAndGearbox(vehicle.engine, vehicle.gearbox));
            store.dispatch(ConfiguratorDuck.setSelectedOptions(selectedOptionIds));
            store.dispatch(FilterDuck.wasSetBudgetType(!hasFinance || paymentJourneyLocked));
            store.dispatch(
                FilterDuck.changeBudgetType(
                    hasFinance || paymentJourneyLockedToFinance ? PaymentJourneyTypes.FINANCE : PaymentJourneyTypes.CASH
                )
            );
            store.dispatch(
                TrimSelectorDuck.setStockTrims({
                    stockTrims: [],
                    totalItems: 0,
                    pageNumber: INITIAL_PAGE_NUMBER,
                    filteredStockTrims: [],
                    totalFilteredItems: 0,
                })
            );
        });

        if (
            !paymentJourneyLocked &&
            [PaymentJourneyTypes.FINANCE, PaymentJourneyTypes.CASH].includes(journeyTypeParam as PaymentJourneyTypes)
        ) {
            if (hasFinance && PaymentJourneyTypes.FINANCE === journeyTypeParam) {
                store.dispatch(FilterDuck.changeBudgetType(PaymentJourneyTypes.FINANCE));
            } else {
                store.dispatch(FilterDuck.changeBudgetType(PaymentJourneyTypes.CASH));
            }

            store.dispatch(FilterDuck.wasSetBudgetType(true));
        }

        return { initialFirstRender, namespacesRequired: ['common'], statusCode, featureContext };
    } catch (e: any) {
        console.error(new Date(), e);

        return { initialFirstRender, query, namespacesRequired: ['common'] };
    }
};
export default ConfiguratorPage;
